/**
 * CamComparisonGrid — the 12-point CAM-vs-LOS compliance summary panel.
 * Build: 2026-06-05-v2
 *
 * The new auditor-facing view for CAM Audit V2. Replaces the 17-point
 * ChecklistGrid when admin config `validation.checklist_mode = 'cam_vs_los'`.
 *
 * Architecture mirrors ChecklistGrid intentionally so both modes feel
 * consistent and so the auditor's mental model carries over:
 *   - Header: score ring + summary stats + Download Report button
 *   - Body:   one row per check, with status badge and "Evidence" action
 *   - Drawer: side-by-side LOS vs CAM evidence (Phase 4 — placeholder for now)
 *
 * The 12 row definitions are kept in this file (not in shared) so the
 * UI labels can evolve independently of rule IDs. CAM_CHECKLIST_RULE_IDS_IN_ORDER
 * from shared is the authoritative ordering; this file just maps each id
 * to its display label, description, and severity color.
 */

import { useMemo, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck, CheckCircle2, XCircle, MinusCircle,
  Download, Eye, FileQuestion, Loader2,
  Upload, Sparkles, Landmark, Check, type LucideIcon,
} from 'lucide-react';
import { Card, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { toast } from '@/acc-ui/primitives/Toast';
import { validationApi } from '@/api/validation-api';
import { losApi } from '@/api/los-api';
import { ingestionApi } from '@/api/ingestion-api';
import { parsingApi } from '@/api/parsing-api';
import { bankingVerificationApi } from '@/api/banking-verification-api';
import { usePermission } from '@/auth/use-permission';
import {
  VALIDATION_VIEW,
  LOS_VIEW,
  SEVERITY,
  type FindingDTO,
  type Severity,
} from '@loanaudit/shared';
import { Link } from 'react-router-dom';
import { generateAndOpenCamReport } from './cam-audit-report';
import { exportFindingsToCSV } from '@/lib/export-findings';
import { CamEvidenceDrawer, type CamRowState } from './CamEvidenceDrawer';

// =========================================================================
// 12-row metadata
// =========================================================================
//
// Each row binds a cam.* rule id to a human-readable label, a short
// description shown beneath it, the data sources it cross-checks, and
// the LOS path string the auditor can use to manually verify in LOS.

export interface CamCheckRow {
  point: number;
  ruleId: string;
  label: string;
  description: string;
  /** Where the data being compared lives, for tooltip + drawer. */
  losPath: string;
  /** Short note shown in the row when the rule is skipped (N/A). */
  skipNote?: string;
}

export const CAM_CHECKLIST_ROWS: readonly CamCheckRow[] = [
  {
    point: 1, ruleId: 'cam.documents_required',
    label: 'Documents Required',
    description: 'Every doc CAM references is on file + KYC per borrower',
    losPath: 'LOS → Document → Digi Document Checklist',
  },
  {
    point: 2, ruleId: 'cam.cibil_score',
    label: 'CIBIL Score',
    description: 'LOS = CAM per applicant + meets product threshold',
    losPath: 'LOS → Decision → CIBIL Report View',
  },
  {
    point: 3, ruleId: 'cam.income_proof_pl_bl',
    label: 'Income Proof — PL / BL',
    description: 'Novel Check banking analysis verified salary credits',
    losPath: 'LOS → Document → Banking Analysis',
    skipNote: 'N/A for non-PL/BL products',
  },
  {
    point: 5, ruleId: 'cam.novel_check',
    label: 'Novel Check',
    description: 'Banking analysis performed + method-specific data',
    losPath: 'LOS → Document → Banking Analysis',
  },
  {
    point: 6, ruleId: 'cam.loan_eligibility',
    label: 'Loan Eligibility Calculation',
    description: 'Amount, ROI, tenure, EMI, FOIR, LTV all match',
    losPath: 'LOS → Detailed Data Entry → Loan Details',
  },
  {
    point: 7, ruleId: 'cam.policy_level_checks',
    label: 'Policy Level Checks',
    description: 'Loan ≤ company cap + age/tenor/geo policy compliance',
    losPath: 'LOS → Detailed Data Entry → Applicant → Company Categorization',
  },
  {
    point: 8, ruleId: 'cam.deviation_matrix',
    label: 'Deviation Matrix',
    description: 'Bidirectional match + approver + mitigant present',
    losPath: 'LOS → Deviation → Approve Deviation View',
  },
  {
    point: 9, ruleId: 'cam.sanction_conditions',
    label: 'Sanction Conditions',
    description: 'Conditions list aligned between LOS and CAM',
    losPath: 'LOS → Underwriting → Sanction Condition View',
  },
  {
    point: 10, ruleId: 'cam.legal_detail',
    label: 'Legal Detail',
    description: 'Legal positive + property/owner match (secured only)',
    losPath: 'Legal Report PDF + CAM',
    skipNote: 'N/A for unsecured loans',
  },
  {
    point: 11, ruleId: 'cam.valuation_report',
    label: 'Valuation Report',
    description: 'Property/amount/date match (secured only)',
    losPath: 'Valuation Report PDF + CAM',
    skipNote: 'N/A for unsecured loans',
  },
  {
    point: 12, ruleId: 'cam.personal_discussion',
    label: 'Personal Discussion',
    description: 'PD conducted + substantive notes',
    losPath: 'CAM → PD section',
  },
] as const;

// =========================================================================
// Row state derivation
// =========================================================================
//
// Each row arrives in one of five states based on the matching finding:
//   - matched          (verdict=compliant, no special title pattern)
//   - deviation_just.  (verdict=compliant + title contains "deviation approved")
//   - mismatch         (verdict=violated)
//   - inconclusive     (verdict=inconclusive, OR no finding + no CAM/LOS data)
//   - skipped          (verdict=compliant + title="N/A for this product")

export type RowState = CamRowState; // alias — kept in sync with the drawer's type

export interface DerivedRow extends CamCheckRow {
  state: RowState;
  severity: Severity | null;
  finding: FindingDTO | null;
}

export function deriveRow(
  row: CamCheckRow,
  findingsByRuleId: Map<string, FindingDTO>,
  hasCamData: boolean,
  hasLosData: boolean,
  productCode: string,
): DerivedRow {
  // Product-based N/A: unsecured loans don't need legal/valuation checks
  const UNSECURED_PRODUCTS = ['PL', 'BL'];
  const SECURED_ONLY_POINTS = [10, 11];
  if (SECURED_ONLY_POINTS.includes(row.point) && UNSECURED_PRODUCTS.includes(productCode.toUpperCase())) {
    const productLabel = productCode.toUpperCase() === 'PL' ? 'Personal Loan' : 'Business Loan';
    const pointLabel = row.point === 10 ? 'Legal Detail' : 'Valuation Report';
    return {
      ...row,
      state: 'skipped',
      severity: null,
      finding: null,
      skipNote: `${pointLabel} not required for ${productLabel} (unsecured)`,
    };
  }

  const finding = findingsByRuleId.get(row.ruleId) ?? null;

  // No finding at all yet — either the rule ran cleanly (compliant —
  // including deviation-justified, which the rules return as compliant)
  // OR the rule wasn't run because CAM/LOS data is missing.
  //
  // Architectural note: compliant verdicts (including "deviation
  // approved" outcomes) are intentionally NOT persisted as findings.
  // The Phase 3 grid can't visually distinguish "clean match" from
  // "deviation justified". Future phase will surface deviation data
  // separately via the evidence drawer.
  if (!finding) {
    if (!hasCamData || !hasLosData) {
      return { ...row, state: 'inconclusive', severity: null, finding: null };
    }
    return { ...row, state: 'matched', severity: null, finding: null };
  }

  // A finding exists — only happens for violated, inconclusive, or error
  // verdicts. We derive state from title + severity since the verdict
  // enum isn't directly exposed on FindingDTO.
  const title = (finding.title ?? '').toLowerCase();

  if (title.includes('n/a')) {
    return { ...row, state: 'skipped', severity: null, finding };
  }
  if (title.includes('cannot evaluate') || title.includes('data missing')) {
    return { ...row, state: 'inconclusive', severity: null, finding };
  }
  // "Deviation Approved: ..." titles signal an approved deviation —
  // breach exists but properly documented and approved.
  if (title.includes('deviation approved')) {
    return { ...row, state: 'deviation_justified', severity: finding.severity, finding };
  }
  // Anything else with a finding is a mismatch (violated).
  return { ...row, state: 'mismatch', severity: finding.severity, finding };
}

// =========================================================================
// Score calculation
// =========================================================================
//
// Score = (matched + deviation_justified) / (matched + deviation_justified
// + mismatched) × 100. Skipped and inconclusive rows are EXCLUDED from the
// denominator so non-applicable checks (e.g. unsecured loan → Point 10) or
// missing data don't artificially lower the score.

export interface ScoreStats {
  score: number;
  matched: number;
  deviation: number;
  mismatch: number;
  inconclusive: number;
  skipped: number;
  evaluable: number;
}

export function computeScore(rows: readonly DerivedRow[]): ScoreStats {
  let matched = 0, deviation = 0, mismatch = 0, inconclusive = 0, skipped = 0;
  for (const r of rows) {
    if (r.state === 'matched') matched++;
    else if (r.state === 'deviation_justified') deviation++;
    else if (r.state === 'mismatch') mismatch++;
    else if (r.state === 'inconclusive') inconclusive++;
    else skipped++;
  }
  const evaluable = matched + deviation + mismatch;
  const score = evaluable === 0 ? 0
    : Math.round(((matched + deviation) / evaluable) * 100);
  return { score, matched, deviation, mismatch, inconclusive, skipped, evaluable };
}

// =========================================================================
// Subcomponents
// =========================================================================

function StateIcon({ state }: { state: RowState }) {
  switch (state) {
    case 'matched':
      return <CheckCircle2 className="w-4 h-4 text-emerald-700" />;
    case 'deviation_justified':
      return <ShieldCheck className="w-4 h-4 text-cyan-700" />;
    case 'mismatch':
      return <XCircle className="w-4 h-4 text-red-700" />;
    case 'inconclusive':
      return <MinusCircle className="w-4 h-4 text-amber-700" />;
    case 'skipped':
      return <FileQuestion className="w-4 h-4 text-slate-500" />;
  }
}

function StateLabel({
  state, severity, skipNote,
}: { state: RowState; severity: Severity | null; skipNote?: string }) {
  switch (state) {
    case 'matched':
      return <Badge tone="success">Matched</Badge>;
    case 'deviation_justified':
      return <Badge tone="info">Deviation OK</Badge>;
    case 'mismatch':
      return (
        <div className="flex items-center gap-1.5">
          <Badge tone="danger">Mismatch</Badge>
          {severity && (
            <span className={`text-caption font-medium ${
              severity === SEVERITY.HIGH || severity === SEVERITY.CRITICAL
                ? 'text-red-700'
                : 'text-amber-700'
            }`}>
              {severity}
            </span>
          )}
        </div>
      );
    case 'inconclusive':
      return <Badge tone="warning">Inconclusive</Badge>;
    case 'skipped':
      return (
        <div className="flex items-center gap-1.5">
          <Badge tone="muted">N/A</Badge>
          {skipNote && (
            <span className="text-caption text-text-tertiary truncate max-w-[140px]">
              {skipNote}
            </span>
          )}
        </div>
      );
  }
}

function ScoreRing({
  score, mismatchCount,
}: { score: number; mismatchCount: number }) {
  // Tier: green ≥90 with zero mismatches, amber 70-89 or any mismatch,
  // red <70.
  const tier =
    mismatchCount > 0
      ? (score >= 70 ? 'amber' : 'red')
      : (score >= 90 ? 'green' : score >= 70 ? 'amber' : 'red');
  const colors = {
    green: { stroke: '#047857', bg: '#d1fae5', text: '#065f46' },
    amber: { stroke: '#d97706', bg: '#fef3c7', text: '#92400e' },
    red:   { stroke: '#b91c1c', bg: '#fee2e2', text: '#991b1b' },
  }[tier];
  return (
    <div
      className="relative flex items-center justify-center shrink-0"
      style={{ width: 80, height: 80 }}
    >
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="34" fill={colors.bg} stroke={colors.stroke} strokeWidth="4" />
      </svg>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center font-bold"
        style={{ color: colors.text }}
      >
        <span className="text-display leading-none">{score}</span>
        <span className="text-micro font-medium opacity-70 mt-0.5">/ 100</span>
      </div>
    </div>
  );
}

// =========================================================================
// MAIN COMPONENT
// =========================================================================

export function CamComparisonGrid({ loanFileId }: { loanFileId: string }) {
  const { has } = usePermission();
  const canSeeValidation = has(VALIDATION_VIEW);
  const canSeeLos = has(LOS_VIEW);

  const qc = useQueryClient();

  // -------- Queries --------
  // Loan-file query first: it polls through the whole pipeline and is the
  // source of truth for "is work still happening". The checklist queries below
  // key their polling off this state rather than their own data, which avoids
  // the chicken-and-egg where a query that loaded before validation started
  // never polls and so never sees the results.
  const PIPELINE_IN_PROGRESS = [
    'accepted', 'virus_scan_pending', 'virus_scanned',
    'parsing_in_progress', 'validation_in_progress',
  ];

  const loanFileQuery = useQuery({
    queryKey: ['loanFile', loanFileId],
    queryFn: () => ingestionApi.get(loanFileId),
    enabled: canSeeValidation,
    refetchInterval: (q) => {
      const state = q.state.data?.state;
      return state && PIPELINE_IN_PROGRESS.includes(state) ? 3000 : false;
    },
  });

  const loanFileState = loanFileQuery.data?.state;
  const pipelineRunning = !!loanFileState && PIPELINE_IN_PROGRESS.includes(loanFileState);

  const validationQuery = useQuery({
    queryKey: ['validation', loanFileId],
    queryFn: () => validationApi.getLoanFileView(loanFileId),
    enabled: canSeeValidation,
    // Poll while ANY pipeline work is happening (driven by the loan-file
    // state, not this query's own), so the 12-point checklist fills in live
    // as validation finishes — no need to leave and re-open the page.
    refetchInterval: () => (pipelineRunning ? 3500 : false),
  });

  // When the loan file reaches a terminal validation state, force one fresh
  // pull of the validation + parsing views so the final checklist is shown
  // even if the last poll landed a moment before completion.
  useEffect(() => {
    if (loanFileState === 'validated' || loanFileState === 'validation_failed') {
      qc.invalidateQueries({ queryKey: ['validation', loanFileId] });
      qc.invalidateQueries({ queryKey: ['parsingView', loanFileId] });
    }
  }, [loanFileState, loanFileId, qc]);

  const losQuery = useQuery({
    queryKey: ['losData', loanFileId],
    queryFn: () => losApi.get(loanFileId),
    enabled: canSeeLos,
  });

  const parsingQuery = useQuery({
    queryKey: ['parsingView', loanFileId],
    queryFn: () => parsingApi.getLoanFileView(loanFileId),
    enabled: canSeeValidation,
    staleTime: 60_000,
  });

  // Banking verifications — newest first; the report uses the most recent
  // completed entry. Returns empty list if no verifications have run yet,
  // which the report renders gracefully (informational note, no broken
  // section). Cached for 60s — same as the parsing view.
  const bankingVerificationQuery = useQuery({
    queryKey: ['bankingVerifications', loanFileId],
    queryFn: () => bankingVerificationApi.list(loanFileId),
    enabled: canSeeValidation,
    staleTime: 60_000,
  });

  // -------- Evidence drawer state (Phase 4 will mount the real drawer) --------
  const [evidenceRuleId, setEvidenceRuleId] = useState<string | null>(null);

  // -------- Derive --------
  const findings = validationQuery.data?.findings ?? [];
  const findingsByRuleId = useMemo(() => {
    const map = new Map<string, FindingDTO>();
    for (const f of findings) {
      // First finding wins. If a rule has multiple, the orchestrator
      // produces only one per rule per run.
      if (!map.has(f.ruleId)) map.set(f.ruleId, f);
    }
    return map;
  }, [findings]);

  // CAM is "present" if there's at least one document classified as cam
  // with a completed extraction in parsingQuery.
  const hasCamData = useMemo(() => {
    const docs = parsingQuery.data?.documents ?? [];
    return docs.some(
      (d) => d.classification?.predictedType === 'cam' && d.extraction?.fields != null,
    );
  }, [parsingQuery.data]);

  const hasLosData = losQuery.data != null;

  const derived = useMemo(
    () => CAM_CHECKLIST_ROWS.map(
      (r) => deriveRow(r, findingsByRuleId, hasCamData, hasLosData, loanFileQuery.data?.productCode ?? ''),
    ),
    [findingsByRuleId, hasCamData, hasLosData, loanFileQuery.data?.productCode],
  );

  const stats = useMemo(() => computeScore(derived), [derived]);

  // -------- Render --------
  if (!canSeeValidation) {
    return (
      <Card>
        <SectionLabel>LOS vs CAM Compliance Audit</SectionLabel>
        <div className="text-caption text-text-tertiary mt-2">
          You don't have permission to view validation findings.
        </div>
      </Card>
    );
  }

  // Still fetching the parsing view — show a loading state rather than
  // flashing the "no CAM extracted yet" guidance before the data settles.
  if (parsingQuery.isLoading) {
    return (
      <Card>
        <SectionLabel>LOS vs CAM Compliance Audit</SectionLabel>
        <div className="mt-2 text-caption text-text-tertiary">Loading compliance audit…</div>
      </Card>
    );
  }

  // No CAM yet — guide the user to upload one. This is the most common
  // first-time state.
  if (!hasCamData) {
    return (
      <Card>
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-brand-blue shrink-0 mt-0.5" />
          <div className="flex-1">
            <SectionLabel className="!m-0">LOS vs CAM Compliance Audit</SectionLabel>
            <div className="mt-2 text-label text-text-secondary">
              No CAM extracted yet. Upload the CAM Report PDF and wait for
              parsing to complete (~30-45 seconds), then attach LOS data
              to start the comparison audit.
            </div>
            <div className="mt-2 text-micro text-text-tertiary">
              Once both are present, the 12 comparison checks will run
              automatically.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Loan file state for progress bar
  const loanState = loanFileQuery.data?.state ?? '';
  const isProcessing = [
    'accepted', 'virus_scan_pending', 'virus_scanned',
    'parsing_in_progress', 'validation_in_progress',
  ].includes(loanState);

  return (
    <Card>
      {/* Pipeline progress bar — shown during processing */}
      {isProcessing && <AuditProgressBar state={loanState} />}

      {/* Header: score + summary stats + download */}
      <div className="flex items-start gap-5 flex-wrap">
        <ScoreRing score={stats.score} mismatchCount={stats.mismatch} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-4 h-4 text-brand-blue" />
            <SectionLabel className="!m-0">LOS vs CAM Compliance Audit</SectionLabel>
            {!hasLosData && (
              <Badge tone="warning" className="ml-1">LOS data missing</Badge>
            )}
          </div>
          <div className="mt-2 flex items-center gap-3 flex-wrap text-label">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
              <span className="font-semibold text-text-primary">{stats.matched}</span>
              <span className="text-text-tertiary">matched</span>
            </span>
            {stats.deviation > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-cyan-700" />
                <span className="font-semibold text-text-primary">{stats.deviation}</span>
                <span className="text-text-tertiary">deviation-justified</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5 text-red-700" />
              <span className="font-semibold text-text-primary">{stats.mismatch}</span>
              <span className="text-text-tertiary">mismatch{stats.mismatch === 1 ? '' : 'es'}</span>
            </span>
            {stats.inconclusive > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <MinusCircle className="w-3.5 h-3.5 text-amber-700" />
                <span className="font-semibold text-text-primary">{stats.inconclusive}</span>
                <span className="text-text-tertiary">inconclusive</span>
              </span>
            )}
            {stats.skipped > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <FileQuestion className="w-3.5 h-3.5 text-slate-500" />
                <span className="font-semibold text-text-primary">{stats.skipped}</span>
                <span className="text-text-tertiary">N/A</span>
              </span>
            )}
          </div>
          <div className="mt-1 text-micro text-text-tertiary">
            {stats.matched + stats.deviation} of {stats.evaluable} evaluable checks pass
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const findings = validationQuery.data?.findings ?? [];
              if (findings.length === 0) {
                toast.info('No findings to export.');
                return;
              }
              const loanNum = loanFileQuery.data?.loanNumber ?? loanFileId.slice(0, 8);
              exportFindingsToCSV(findings, `findings-${loanNum}.csv`);
            }}
            disabled={!validationQuery.data?.findings?.length}
            title="Download findings as CSV for spreadsheet review"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Export CSV
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              const lf = loanFileQuery.data;
              if (!lf) {
                toast.error('Loan file data not loaded yet. Try refreshing the page and clicking again.');
                return;
              }
              try {
                generateAndOpenCamReport({
                  loanFile: {
                    id: lf.id,
                    loanNumber: lf.loanNumber,
                    borrowerName: lf.borrowerName,
                    productCode: lf.productCode,
                    branchCode: lf.branchCode,
                    state: lf.state,
                  },
                  findings: validationQuery.data?.findings ?? [],
                  losData: losQuery.data ?? null,
                  parsingView: parsingQuery.data ?? null,
                  documents: (lf.documents ?? []).map((d) => ({
                    id: d.id,
                    originalFileName: d.originalFileName,
                    documentType: d.documentType,
                  })),
                  validatedAt: validationQuery.data?.latestRun?.finishedAt ?? null,
                  bankingVerification:
                    bankingVerificationQuery.data?.verifications?.find(
                      (v) => v.status === 'completed',
                    ) ?? null,
                });
              } catch (err) {
                // Outermost safety net — generateAndOpenCamReport has its own
                // try/catch for the HTML build; this catches sync errors here.
                // eslint-disable-next-line no-console
                console.error('[Download Report] outer catch:', err);
                toast.error(`Could not generate report: ${err instanceof Error ? err.message : String(err)}`);
              }
            }}
            disabled={!loanFileQuery.data || validationQuery.isLoading}
            title="Open a print-ready CAM-vs-LOS audit report in a new tab"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Download Report
          </Button>
        </div>
      </div>

      {/* The 12-row grid */}
      <div className="mt-4 border border-glass-hairline rounded-md overflow-hidden">
        <ul className="divide-y divide-glass-hairline">
          {derived.map((row, idx) => (
            <CamCheckRowItem
              key={row.point}
              displayIndex={idx + 1}
              row={row}
              onViewEvidence={(ruleId) => setEvidenceRuleId(ruleId)}
            />
          ))}
        </ul>
      </div>

      {/* Side-by-side LOS vs CAM evidence drawer (Phase 4). Mounted always,
          opens when evidenceRuleId is set. Renders into a right-side sheet
          via the existing Sheet primitive. */}
      <CamEvidenceDrawer
        open={evidenceRuleId !== null}
        onClose={() => setEvidenceRuleId(null)}
        loanFileId={loanFileId}
        point={evidenceRuleId
          ? (derived.find((r) => r.ruleId === evidenceRuleId)?.point ?? null)
          : null
        }
        label={evidenceRuleId
          ? (derived.find((r) => r.ruleId === evidenceRuleId)?.label ?? '')
          : ''
        }
        state={evidenceRuleId
          ? (derived.find((r) => r.ruleId === evidenceRuleId)?.state ?? 'inconclusive')
          : 'inconclusive'
        }
        finding={evidenceRuleId
          ? (derived.find((r) => r.ruleId === evidenceRuleId)?.finding ?? null)
          : null
        }
        losData={losQuery.data ?? null}
        parsingView={parsingQuery.data ?? null}
        documents={(loanFileQuery.data?.documents ?? []).map((d) => ({
          id: d.id,
          originalFileName: d.originalFileName,
          documentType: d.documentType,
        }))}
        losPath={evidenceRuleId
          ? (derived.find((r) => r.ruleId === evidenceRuleId)?.losPath ?? '')
          : ''
        }
      />
    </Card>
  );
}

// =========================================================================
// CamCheckRowItem
// =========================================================================

function CamCheckRowItem({
  row,
  displayIndex, onViewEvidence,
}: {
  row: DerivedRow;
  displayIndex: number;
  onViewEvidence: (ruleId: string) => void;
}) {
  return (
    <li title={row.losPath}>
      <div className="flex items-center gap-3 px-3 py-2 hover:bg-surface-variant/50 transition-colors">
        <div className="w-6 text-center text-caption font-semibold text-text-tertiary tabular-nums shrink-0">
          {displayIndex}
        </div>
        <div className="shrink-0">
          <StateIcon state={row.state} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-label font-medium text-text-primary truncate">
            {row.label}
          </div>
          {/* Only show the one-line reason where it adds value (problems). Matched
              rows stay clean — the full detail is always in the Evidence drawer. */}
          {row.state !== 'matched' && (
            <div className="text-caption text-text-tertiary truncate">
              {row.finding?.description ?? row.description}
            </div>
          )}
        </div>
        <div className="shrink-0">
          <StateLabel
            state={row.state}
            severity={row.severity}
            skipNote={row.skipNote}
          />
        </div>
        <div className="shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => onViewEvidence(row.ruleId)}
            className="inline-flex items-center gap-1 text-caption font-medium text-brand-blue hover:bg-brand-blue/[0.06] px-2 py-1 rounded-md transition-colors"
            title="View evidence for this check"
            aria-label={`View evidence for point ${row.point}`}
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Evidence</span>
          </button>
          {row.state === 'mismatch' && row.finding && (
            <Link
              to={`/findings/${row.finding.id}`}
              className="text-caption font-medium text-red-700 hover:bg-red-50 px-2 py-1 rounded-md transition-colors"
              title="Open finding for review"
            >
              Review →
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}

// =========================================================================
// Audit Progress Bar — shown during pipeline processing
// =========================================================================

const PIPELINE_STEPS: Array<{ key: string; label: string; icon: LucideIcon }> = [
  { key: 'upload', label: 'Upload', icon: Upload },
  { key: 'parsing', label: 'Parsing', icon: Sparkles },
  { key: 'verification', label: 'Banking Verification', icon: Landmark },
  { key: 'validation', label: 'Audit Rules', icon: ShieldCheck },
];

function getStepStatus(stepKey: string, state: string): 'done' | 'active' | 'pending' {
  const stateToStep: Record<string, number> = {
    accepted: 0,
    virus_scan_pending: 0,
    virus_scanned: 0,
    parsing_in_progress: 1,
    parsed: 2,
    validation_in_progress: 3,
    validated: 4,
    validation_failed: 4,
  };
  const currentIdx = stateToStep[state] ?? 0;
  const stepIdx = PIPELINE_STEPS.findIndex((s) => s.key === stepKey);
  if (stepIdx < currentIdx) return 'done';
  if (stepIdx === currentIdx) return 'active';
  return 'pending';
}

function AuditProgressBar({ state }: { state: string }) {
  return (
    <div className="mb-3 rounded-xl border border-outline-variant bg-surface-container-low p-4">
      <div className="flex items-center gap-1 mb-3">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-blue" />
        <span className="text-caption font-semibold text-brand-blue">
          Processing loan file…
        </span>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-0">
        {PIPELINE_STEPS.map((step, i) => {
          const status = getStepStatus(step.key, state);
          return (
            <div key={step.key} className="flex items-center flex-1">
              {/* Step circle + label */}
              <div className="flex flex-col items-center gap-1 flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-label transition-all duration-500 ${
                    status === 'done'
                      ? 'bg-emerald-100 border-2 border-emerald-500 text-emerald-700'
                      : status === 'active'
                        ? 'bg-brand-blue/10 border-2 border-brand-blue text-brand-blue shadow-sm shadow-brand-blue/20 animate-pulse'
                        : 'bg-slate-100 border-2 border-slate-300 text-slate-400'
                  }`}
                >
                  {status === 'done'
                    ? <Check className="w-4 h-4" />
                    : <step.icon className="w-4 h-4" />}
                </div>
                <span
                  className={`text-micro font-medium text-center leading-tight ${
                    status === 'done'
                      ? 'text-emerald-700'
                      : status === 'active'
                        ? 'text-brand-blue font-semibold'
                        : 'text-slate-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line (not after last) */}
              {i < PIPELINE_STEPS.length - 1 && (
                <div className="flex-shrink-0 w-8 h-[2px] -mt-4 mx-[-4px]">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      getStepStatus(PIPELINE_STEPS[i + 1]!.key, state) !== 'pending'
                        ? 'bg-emerald-400'
                        : status === 'active'
                          ? 'bg-gradient-to-r from-brand-blue to-slate-300'
                          : 'bg-slate-200'
                    }`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-brand-blue to-emerald-500 transition-all duration-1000 ease-out"
          style={{
            width:
              state === 'accepted' || state === 'virus_scan_pending' || state === 'virus_scanned' ? '15%'
              : state === 'parsing_in_progress' ? '45%'
              : state === 'parsed' ? '70%'
              : state === 'validation_in_progress' ? '85%'
              : '100%',
          }}
        />
      </div>
    </div>
  );
}

